import { defineStore } from "pinia";
import {
  collection,
  query,
  getFirestore,
  getDocs,
  orderBy,
  where,
  doc,
  setDoc,
  updateDoc,
  deleteDoc
} from "firebase/firestore";
import { userUserStore } from "../stores/user";
import { Mesa, mesaConverter, MesaUbicacion } from "@fixeduy/restaurantestypes";

/** Presets de tamaño visual en el mapa (por cantidad de personas). */
export const PRESET_TAMANOS_MESA = [
  { personas: 2, ancho: 10, alto: 10 },
  { personas: 4, ancho: 14, alto: 14 },
  { personas: 6, ancho: 18, alto: 18 },
  { personas: 8, ancho: 22, alto: 22 }
] as const;

export const useMesasStore = defineStore({
  id: "mesas",
  state: () => {
    return {
      mesas: [] as Mesa[],
      cargando: false
    };
  },
  getters: {
    mesasOrdenadas(): Mesa[] {
      return [...this.mesas].sort((a, b) => a.orden - b.orden);
    },
    mesasConUbicacion(): Mesa[] {
      return this.mesas.filter(m => m.ubicacion != null);
    },
    /** Mesas con estado ocupada/ocupado. */
    mesasOcupadas(): Mesa[] {
      return this.mesas.filter(m => {
        const e = (m.estado || "").toLowerCase();
        return e === "ocupada" || e === "ocupado";
      });
    },
    /** Mesas con ubicación, opcionalmente filtradas por sectorId. null = todas. */
    mesasConUbicacionPorSector(): (sectorId: string | null) => Mesa[] {
      return (sectorId: string | null) => {
        const conUbicacion = this.mesas.filter(m => m.ubicacion != null);
        if (sectorId == null || sectorId === "") return conUbicacion;
        return conUbicacion.filter(m => m.sectorId === sectorId);
      };
    }
  },
  actions: {
    async getFromFirestore() {
      const db = getFirestore();
      const userStore = userUserStore();
      const empresaId = userStore.empresa?.id;
      if (!empresaId) return;

      this.cargando = true;
      try {
        const collectionRoute = `empresas/${empresaId}/mesas`;
        const q = query(
          collection(db, collectionRoute),
          orderBy("orden")
        ).withConverter(mesaConverter);

        const querySnapshot = await getDocs(q);
        this.mesas = querySnapshot.docs.map(doc => doc.data());

      // Migración: si hay mesas legacy con `sectorId` vacío/null,
      // las reasignamos al sector "General" (id real).
      const mesasSinSector = this.mesas.filter(m => !m.sectorId);
      if (mesasSinSector.length) {
        const generalQ = query(
          collection(db, `empresas/${empresaId}/sectores`),
          where("nombre", "==", "General"),
          where("activo", "==", true)
        );
        const generalSnap = await getDocs(generalQ);
        const generalId = generalSnap.docs[0]?.id;

        if (generalId) {
          await Promise.all(
            mesasSinSector.map(m =>
              updateDoc(
                doc(db, `empresas/${empresaId}/mesas`, m.id),
                { sectorId: generalId }
              )
            )
          );

          this.mesas = this.mesas.map(m =>
            !m.sectorId ? { ...m, sectorId: generalId } : m
          );
        }
      }
      } finally {
        this.cargando = false;
      }
    },
    getById(id: string): Mesa | null {
      return this.mesas.find(m => m.id === id) ?? null;
    },
    ubicacionDefaultParaNuevaMesa(
      orden: number,
      size?: { ancho: number; alto: number }
    ): MesaUbicacion {
      const col = orden % 5;
      const fila = Math.floor(orden / 5);
      const paso = 18;
      const ancho = size?.ancho ?? 12;
      const alto = size?.alto ?? 12;
      return {
        x: 5 + col * paso,
        y: 8 + fila * paso,
        ancho,
        alto
      };
    },
    async updateUbicacion(
      mesaId: string,
      ubicacion: MesaUbicacion
    ): Promise<boolean> {
      const userStore = userUserStore();
      const empresaId = userStore.empresa?.id;
      if (!empresaId) return false;

      const db = getFirestore();
      const docRef = doc(db, `empresas/${empresaId}/mesas`, mesaId);
      const mesa = this.getById(mesaId);
      if (!mesa) return false;

      try {
        await updateDoc(docRef, { ubicacion });
        const idx = this.mesas.findIndex(m => m.id === mesaId);
        if (idx !== -1) {
          this.mesas[idx] = { ...this.mesas[idx], ubicacion };
        }
        return true;
      } catch (error) {
        console.error("Error al actualizar ubicación de mesa:", error);
        return false;
      }
    },
    async updateMesa(
      mesaId: string,
      datos: { nombre?: string; activo?: boolean; sectorId?: string | null }
    ): Promise<true | false | string> {
      const userStore = userUserStore();
      const empresaId = userStore.empresa?.id;
      if (!empresaId) return false;

      const mesa = this.getById(mesaId);
      if (!mesa) return false;

      const db = getFirestore();
      const docRef = doc(db, `empresas/${empresaId}/mesas`, mesaId);

      // Validación: evitar duplicados por nombre (case-insensitive), incluyendo inactivas.
      if (typeof datos.nombre === "string") {
        const nombreTrim = datos.nombre.trim();
        const nombreLower = nombreTrim.toLowerCase();
        if (nombreLower) {
          const collectionRoute = `empresas/${empresaId}/mesas`;
          const q = query(collection(db, collectionRoute)).withConverter(
            mesaConverter
          );
          const snapshot = await getDocs(q);

          const existe = snapshot.docs.some(d => {
            // Excluimos la mesa actual por id del doc (no por campo id).
            if (d.id === mesaId) return false;
            const n = (d.data().nombre ?? "").trim().toLowerCase();
            return n === nombreLower;
          });

          if (existe) {
            return `Ya existe una mesa con el nombre "${nombreTrim}".`;
          }
        }
      }

      try {
        await updateDoc(docRef, datos);
        const idx = this.mesas.findIndex(m => m.id === mesaId);
        if (idx !== -1) {
          const updated = { ...this.mesas[idx], ...datos };
          if (updated.sectorId === null) updated.sectorId = undefined;
          this.mesas[idx] = updated as Mesa;
        }
        return true;
      } catch (error) {
        console.error("Error al actualizar mesa:", error);
        return false;
      }
    },
    async updateEstado(mesaId: string, estado: string): Promise<boolean> {
      const userStore = userUserStore();
      const empresaId = userStore.empresa?.id;
      if (!empresaId) return false;

      const mesa = this.getById(mesaId);
      if (!mesa) return false;

      const docRef = doc(getFirestore(), `empresas/${empresaId}/mesas`, mesaId);
      try {
        await updateDoc(docRef, { estado });
        const idx = this.mesas.findIndex(m => m.id === mesaId);
        if (idx !== -1) {
          this.mesas[idx] = { ...this.mesas[idx], estado } as Mesa;
        }
        return true;
      } catch (error) {
        console.error("Error al actualizar estado de mesa:", error);
        return false;
      }
    },
    async eliminarMesa(mesaId: string): Promise<boolean> {
      const userStore = userUserStore();
      const empresaId = userStore.empresa?.id;
      if (!empresaId) return false;

      const docRef = doc(getFirestore(), `empresas/${empresaId}/mesas`, mesaId);
      try {
        await deleteDoc(docRef);
        this.mesas = this.mesas.filter(m => m.id !== mesaId);
        return true;
      } catch (error) {
        console.error("Error al eliminar mesa:", error);
        return false;
      }
    },
    async crearMesa(
      nombre: string,
      capacidad: number,
      orden: number,
      sectorId?: string | null,
      presetPersonas?: number
    ): Promise<true | false | string> {
      const userStore = userUserStore();
      const empresaId = userStore.empresa?.id;
      if (!empresaId) return false;

      const db = getFirestore();
      const collectionRoute = `empresas/${empresaId}/mesas`;
      const docRef = doc(collection(db, collectionRoute));

      const nombreTrim = nombre.trim();
      const nombreLower = nombreTrim.toLowerCase();

      const ordenNum = Number(orden) ?? this.mesas.length;
      const size =
        presetPersonas != null
          ? PRESET_TAMANOS_MESA.find(p => p.personas === presetPersonas) ?? undefined
          : undefined;
      const ubicacion = this.ubicacionDefaultParaNuevaMesa(ordenNum, size);

      const nuevaMesa: Mesa = {
        id: docRef.id,
        nombre: nombreTrim,
        capacidad: Number(capacidad) || 0,
        estado: "disponible",
        activo: true,
        orden: ordenNum,
        version: 1,
        ubicacion,
        ...(sectorId ? { sectorId } : {})
      };

      this.cargando = true;
      try {
        // Validación: evitar duplicados por nombre (case-insensitive), incluyendo mesas inactivas.
        const q = query(collection(db, collectionRoute)).withConverter(mesaConverter);
        const snapshot = await getDocs(q);
        const existe = snapshot.docs.some(d => {
          const n = (d.data().nombre ?? "").trim().toLowerCase();
          return n === nombreLower;
        });

        if (existe) {
          return `Ya existe una mesa con el nombre "${nombreTrim}".`;
        }

        await setDoc(docRef, nuevaMesa);
        this.mesas.push(nuevaMesa);
        return true;
      } catch (error) {
        console.error("Error al crear mesa:", error);
        return false;
      } finally {
        this.cargando = false;
      }
    }
  }
});
